/**
 * Шаблоны описаний совместимости
 * Используются для генерации текста в модалке "See compatibility details"
 */

const POSITIVES_ELEMENT_PLUS_1 = [
  "You naturally resonate with each other's energy. Conversations may flow easily, even without much effort at the beginning. This kind of alignment often creates a sense of comfort early on.",
  "There's an intuitive understanding between you that can make interactions feel smooth. You may quickly notice that you react to situations in similar ways. This can help build trust faster.",
  "Your temperaments align in a way that supports mutual attraction. Being together may feel balanced rather than draining. This creates a good foundation for connection.",
  "You tend to approach life and relationships with a compatible sense of pace. This makes it easier to meet each other halfway. Over time, this can reduce unnecessary friction.",
  "There's a natural harmony in how you express yourselves. You may feel understood without having to explain everything. That ease can make spending time together enjoyable.",
  "Your energies complement each other in a way that feels supportive. One doesn't overpower the other. This balance often helps connections feel stable.",
  "You're likely to feel on the same wavelength in everyday situations. Small decisions and reactions may feel surprisingly aligned. This can strengthen the sense of closeness.",
  "This match can feel natural rather than forced. Interactions may unfold smoothly as you get to know each other. That sense of ease can help things develop organically.",
  "There's a shared emotional or motivational tone between you. This can help you understand what drives the other person. Such alignment often supports long-term interest.",
  "You may find it easier than usual to sync with each other's moods. Emotional exchanges can feel more fluid. This supports deeper connection over time."
]

const POSITIVES_ELEMENT_ZERO = [
  "You bring different qualities into the connection, which can make it engaging. Each of you may notice strengths the other naturally offers. This contrast can keep things interesting.",
  "Your differences may encourage curiosity rather than tension. You might enjoy learning how the other sees the world. This can add depth to the connection.",
  "This match has room for growth through exploration. You may not always react the same way, but that can be enriching. Openness plays a key role here.",
  "You complement each other by focusing on different aspects of life. This can create a well-rounded dynamic. Balance often comes from appreciating these differences.",
  "Your connection may feel fresh and dynamic. You're unlikely to fall into predictable patterns too quickly. That variety can keep the interaction alive.",
  "You approach situations differently, which can broaden both perspectives. This encourages flexibility. Mutual respect helps this pairing work well.",
  "There's potential for balance if you stay open to adaptation. Neither approach is better — just different. This mindset supports harmony.",
  "This pairing invites learning rather than instant understanding. Over time, differences can become strengths. Patience allows that process to unfold.",
  "Your contrast can spark thoughtful conversations. You may challenge each other's привычные взгляды. That can deepen mutual interest.",
  "This match benefits from curiosity and communication. When you explore differences instead of avoiding them, connection grows naturally."
]

const POSITIVES_ELEMENT_CLASHING = [
  "Your differences can create strong attraction through contrast. You may feel drawn to what the other does differently. This dynamic often brings intensity.",
  "This connection may feel lively and noticeable from the start. Opposite styles can spark interest quickly. That energy can be exciting.",
  "You might challenge each other in ways that provoke growth. This can push both of you out of привычных рамок. Awareness helps turn tension into curiosity.",
  "There's potential for excitement where your approaches differ. Interactions may feel emotionally charged. This can keep the connection engaging.",
  "Your pairing may feel intense rather than calm. Strong reactions are possible, both positive and challenging. That intensity can deepen connection if handled thoughtfully.",
  "This match invites you to explore unfamiliar territory. Differences may highlight new sides of yourself. Growth often comes from contrast.",
  "You bring very different energies into the connection. That contrast can feel stimulating rather than boring. Balance comes with understanding.",
  "Interactions may feel dynamic and unpredictable. This can create memorable moments together. Openness helps manage the intensity.",
  "This pairing can feel powerful when differences are embraced. Resistance may create tension, while acceptance creates attraction. Choice matters here.",
  "Your connection encourages awareness of how you relate to others. Differences become part of the experience. This can make the bond more meaningful."
]

const POSITIVES_MODALITY_PLUS_1 = [
  "You tend to move through life at a similar rhythm. This can make planning and communication easier. Shared timing often reduces friction.",
  "Your approaches to change and decision-making feel aligned. You may feel comfortable with each other's pace. This supports stability.",
  "There's a natural sense of timing between you. You're likely to understand when to act and when to wait. This creates flow.",
  "You react to situations with a comparable level of intensity. This helps you anticipate each other's needs. Coordination becomes easier.",
  "Your life tempo feels compatible. This can make everyday interactions smoother. It supports consistency.",
  "You may find it easier to align on priorities. Similar rhythms help avoid unnecessary tension. This supports long-term potential.",
  "You approach progress in a comparable way. This helps you move forward together. Shared pace builds trust.",
  "Your timing around change feels synchronized. That alignment can feel reassuring. It supports mutual understanding.",
  "You tend to handle transitions similarly. This reduces surprises. Stability grows from that familiarity.",
  "There's a shared sense of momentum between you. Neither feels rushed or held back. This balance supports connection."
]

const KEEP_IN_MIND_ELEMENT_CLASHING = [
  "You may need a bit more patience with each other's natural style. Reactions can differ more than expected. Clear communication helps bridge that gap.",
  "Differences in emotional expression may surface. This doesn't mean incompatibility, but awareness is important. Taking time to explain feelings can help.",
  "You might occasionally misunderstand each other's intentions. Pausing before reacting can make a big difference. Clarity builds trust here.",
  "This connection benefits from conscious communication. Assumptions may lead to tension. Openness helps keep things balanced.",
  "Your contrasting styles may require adjustment. Small compromises can improve harmony. Mutual respect is key.",
  "Strong reactions are possible when perspectives differ. Giving each other space can help. Balance grows with understanding.",
  "You may need to slow down and listen carefully at times. Differences don't disappear on their own. Attention makes them manageable.",
  "This match works best with emotional awareness. Being honest about needs reduces friction. Patience supports growth.",
  "Misalignment can happen if expectations aren't clear. Checking in regularly helps. Communication keeps things grounded.",
  "You may need to be mindful of how you express frustration. Tone and timing matter. Thoughtfulness strengthens the bond."
]

const KEEP_IN_MIND_ELEMENT_NEUTRAL = [
  "Small differences can shape the dynamic over time. Talking things through helps maintain balance. Communication keeps the connection healthy.",
  "Like any connection, this one grows through honesty. Staying open about expectations helps. Clarity supports trust.",
  "Differences may appear gradually. That's a normal part of getting closer. Awareness helps you navigate them smoothly.",
  "Taking time to listen can deepen understanding. Mutual respect supports the bond. This keeps things steady.",
  "This match benefits from ongoing communication. Feelings evolve with time. Staying present strengthens connection.",
  "Even good matches require attention. Checking in with each other helps. Small efforts matter.",
  "Openness about needs supports long-term potential. Silence can create distance. Dialogue keeps things aligned.",
  "Growth happens through shared awareness. Understanding develops step by step. Patience supports that process.",
  "Balance comes from mutual understanding. Paying attention to each other's signals helps. This strengthens trust.",
  "Connection deepens when both sides stay engaged. Listening matters as much as speaking. This keeps things grounded."
]

/**
 * Получить случайный элемент из массива (детерминированно по userAId и userBId)
 */
function getRandomItem(array, userAId, userBId, index) {
  const hash = (str) => {
    let hash = 0
    for (let i = 0; i < str.length; i++) {
      const char = str.charCodeAt(i)
      hash = ((hash << 5) - hash) + char
      hash = hash & hash
    }
    return Math.abs(hash)
  }
  
  const seed = hash(`${userAId}_${userBId}_${index}`)
  return array[seed % array.length]
}

/**
 * Генерировать описание совместимости
 * @param {number} elementScore - результат getElementScore (1, 0, или 1 для clashing)
 * @param {number} modalityScore - результат getModalityScore (1 или 0)
 * @param {string} userAId - ID первого пользователя
 * @param {string} userBId - ID второго пользователя
 * @returns {Object} Объект с positives и keepInMind массивами
 */
export function generateCompatibilityDescription(elementScore, modalityScore, userAId, userBId) {
  const positives = []
  let keepInMind = []

  // Выбираем первый positive на основе elementScore
  if (elementScore === 1) {
    positives.push(getRandomItem(POSITIVES_ELEMENT_PLUS_1, userAId, userBId, 0))
  } else if (elementScore === 0) {
    positives.push(getRandomItem(POSITIVES_ELEMENT_ZERO, userAId, userBId, 0))
  } else {
    // elementScore === 1 (clashing)
    positives.push(getRandomItem(POSITIVES_ELEMENT_CLASHING, userAId, userBId, 0))
  }

  // Если есть modalityScore = +1, добавляем второй positive
  if (modalityScore === 1) {
    positives.push(getRandomItem(POSITIVES_MODALITY_PLUS_1, userAId, userBId, 1))
  }

  // Выбираем keepInMind
  if (elementScore === 1) {
    // Clashing - обязательный keepInMind
    keepInMind = [getRandomItem(KEEP_IN_MIND_ELEMENT_CLASHING, userAId, userBId, 2)]
  } else {
    // elementScore = 0 или +1
    keepInMind = [getRandomItem(KEEP_IN_MIND_ELEMENT_NEUTRAL, userAId, userBId, 2)]
  }

  return {
    positives,
    keepInMind
  }
}
